import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  forwardRef,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { ChipComponent } from '../chip/chip.component';
import { provideIcons } from '@ng-icons/core';
import { heroChevronUpDown } from '@ng-icons/heroicons/outline';

interface Option {
  value: string;
  label: string;
  description?: string;
  icon?: string;
  selected?: boolean;
}

@Component({
  selector: 'ui-multi-select',
  templateUrl: './multi-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
  viewProviders: [
    provideIcons({
      heroChevronUpDown,
    }),
  ],
  imports: [CommonModule, ReactiveFormsModule, ChipComponent],
})
export class MultiSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  private eRef = inject(ElementRef);

  _options: Option[] = [];
  readonly placeholder = input('Select option...');
  readonly withChips = input(true); // Change this to false to display comma-separated values
  @Input() disabled = false;
  focus = false;

  get options(): Option[] {
    return this._options;
  }

  @Input()
  set options(value: Option[]) {
    this._options = JSON.parse(JSON.stringify(value)) as Option[];
    this.selectedOptions = [];
    this.updateOptions();
  }

  formControl = new FormControl({ value: '', disabled: false }); //form control for the custom component

  dropdownVisible = false;
  filteredOptions: Option[] = [];
  selectedOptions: Option[] = [];
  searchTerm = new FormControl<string | undefined>({
    value: '',
    disabled: this.disabled,
  });

  private onChange: (value: string[]) => void = () => {
    //
  };
  private onTouched: () => void = () => {
    //
  };

  ngOnInit() {
    this.filteredOptions = this._options;
    // Subscribe to valueChanges observable
    this.searchTerm.valueChanges.subscribe((value) => {
      this.filterOptions(value);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled']) {
      this.setDisabledState(this.disabled);
    }
  }

  readonly searchInput = viewChild.required<ElementRef>('searchInput');
  readonly buttonContainer = viewChild.required<ElementRef>('button');

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.dropdownVisible = false;
      this.focus = false;
    }
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    if (this.disabled) return;

    this.dropdownVisible = !this.dropdownVisible;
    this.dropdownVisible ? this.openDropdown() : this.closeDropdown();
  }

  openDropdown() {
    this.dropdownVisible = true;
    this.filterOptions();
    this.focus = true;
    this.searchInput().nativeElement.focus();
  }

  private closeDropdown() {
    this.dropdownVisible = false;
    this.focus = false;
    this.buttonContainer().nativeElement.blur();
  }

  handleFocusOnButton() {
    if (this.disabled) return;
    this.focus = true;
    this.searchInput().nativeElement.focus();
  }

  showDropdown() {
    if (this.disabled) return;
    this.dropdownVisible = true;
  }

  get selectedLabels(): string {
    return this.selectedOptions.map((option) => option.label).join(', ');
  }

  filterOptions(searchTerm: string | null = ''): void {
    if (!searchTerm) {
      this.filteredOptions = [...this.options];
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();

    this.filteredOptions = this.options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchTermLower) ||
        (option.description?.toLowerCase().includes(searchTermLower) ?? false)
    );
  }

  selectOption(event: MouseEvent, option: Option) {
    event.stopPropagation();
    if (this.disabled) return;
    const index = this.selectedOptions.findIndex(
      (o) => o.value === option.value
    );
    if (index !== -1) {
      // Option is already selected, remove it
      this.selectedOptions = this.selectedOptions.filter((_, i) => i !== index);
    } else {
      // Option is not selected, add it
      this.selectedOptions = [
        ...this.selectedOptions,
        { ...option, selected: true },
      ];
    }
    this.onChange(this.selectedOptions.map((o) => o.value));
    this.updateOptions();
    this.filterOptions();
    this.searchTerm.setValue('');
    this.searchInput().nativeElement.focus();
  }

  removeOption(option: Option) {
    if (this.disabled) return;
    this.selectedOptions = this.selectedOptions
      .map((opt) => {
        if (opt.value === option.value) {
          opt.selected = false;
        }
        return opt;
      })
      .filter((o) => o.value !== option.value);
    this.onChange(this.selectedOptions.map((o) => o.value));
    this.updateOptions();
    this.filterOptions();
  }

  updateOptions() {
    this._options = this._options.map((option) => ({
      ...option,
      selected: !!this.selectedOptions.find((o) => o.value === option.value),
    }));
  }

  writeValue(values: string[]): void {
    //
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.searchTerm.disable();
    } else {
      this.searchTerm.enable();
    }
  }
}
